import * as Sentry from "@sentry/react";

import {
  getCollectionOfDittoComponents,
  useTexts,
} from "Components/TextContext";
import { Form, FormikProps } from "formik";
import { useDispatch, useSelector } from "react-redux";

import { StyledAlert } from "App/global-style";
import { MarketingConsent } from "Components/Consent/MarketingConsent";
import { ProductIntroTile } from "Components/Features/CheckPrice/Tiles/ProductIntroTile";
import { PartnerProductIntroForms } from "Components/Features/CheckPrice/Tiles/ProductIntroTile/PartnerProductIntroForms/PartnerProductIntroForms";
import { AlertType } from "Components/Form/Alert";
import { SystemErrorAlert } from "Components/Form/Alert/SystemErrorAlert";
import { ErrorsSummedUp } from "Components/Form/ErrorsSummedUp";
import { InputFailureTracker } from "Components/InputFailureTracker";
import { NavigationButtons } from "Components/NavigationButtons";
import { ScrollToFirstError } from "Components/ScrollToFirstError";
import { TextComponentNameLevel1 } from "Components/TextContext/textConstants";
import { IllustrationName } from "Constants/constants";
import { MAX_AGE_HEALTH_INSURANCE_CUSTOMER } from "Features/CheckPrice/Products/productConstants";
import { ProductBackendName } from "Features/CheckPrice/Products/productTypes";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { useFormState } from "Store/priceCalculation/priceCalculationHooks";
import { usePriceCalculationSelector } from "Store/priceCalculation/priceCalculationSelectors";
import { updatePriceCalculationFormState } from "Store/priceCalculation/priceCalculationSlice";
import { nextStep } from "Store/steps/stepsSlice";
import { customerDateOfBirthSelector } from "Store/userIdentification/userIdentificationSelectors";
import { mapValidationErrorsDitto } from "Utils/formUtils";
import React from "react";
import { isCustomerTooOld } from "../../../utils";
import { HEALTH_ABOUT_YOU_STEP } from "../healthSteps";
import { HealthProductIntroFormElements } from "./HealthProductIntroFormElements";
import { HEALTH_PRODUCT_INTRO_INITIAL_FORM_VALUES } from "./healthProductIntroConstants";
import { HealthProductIntroFormValues } from "./healthProductIntroInterfaces";
import { validationSchema } from "./healthProductIntroValidation";

export const HealthProductIntro = (): React.ReactElement => {
  const dispatch = useDispatch();
  const { priceCalculationResult } = usePriceCalculationSelector();
  const initialFormState = useFormState<HealthProductIntroFormValues>(
    HEALTH_ABOUT_YOU_STEP.id,
    HEALTH_PRODUCT_INTRO_INITIAL_FORM_VALUES,
  );

  const { getText } = useTexts();

  const customerDateOfBirth = useSelector(customerDateOfBirthSelector);
  const isOldCustomer = isCustomerTooOld(
    MAX_AGE_HEALTH_INSURANCE_CUSTOMER,
    customerDateOfBirth,
  );

  const { shorttitle: shortTitleComponents } = getCollectionOfDittoComponents(
    TextComponentNameLevel1.HEALTH,
  );

  const handleSubmit = (form: HealthProductIntroFormValues): void => {
    dispatch(
      updatePriceCalculationFormState({
        stepId: HEALTH_ABOUT_YOU_STEP.id,
        formState: form,
      }),
    );

    dispatch(nextStep());
  };

  return (
    <Sentry.ErrorBoundary
      beforeCapture={(scope) => {
        scope.setTag(
          "location",
          `CheckPrice - ProductIntro - ${INSURANCE_NAME_VALUE.HEALTH_WITHOUT_SIGNING}`,
        );
      }}
    >
      <ProductIntroTile
        illustrationName={IllustrationName.ESTABLISHMENT_FAMILY}
        productBackendName={ProductBackendName.HEALTH}
        validationSchema={validationSchema()}
        initialValues={initialFormState}
        onSubmit={handleSubmit}
      >
        {(formProps: FormikProps<HealthProductIntroFormValues>) => {
          return (
            <Form>
              {isOldCustomer ? (
                <StyledAlert
                  title={getText("healthform.alerttitle.customertooold")}
                  type="error"
                  alertType={AlertType.STOPPER_ALERT}
                  fluid
                  marginTop="32px"
                />
              ) : (
                <>
                  <HealthProductIntroFormElements formProps={formProps} />
                  <PartnerProductIntroForms />
                  <MarketingConsent />
                  {priceCalculationResult.error && (
                    <SystemErrorAlert alertId="priceCalculation" />
                  )}
                  <ScrollToFirstError />
                  <ErrorsSummedUp
                    errors={mapValidationErrorsDitto(
                      formProps.errors,
                      shortTitleComponents,
                    )}
                  />
                </>
              )}

              <NavigationButtons
                hidePreviousButton
                isLoading={priceCalculationResult.isLoading}
                disableNextButton={isOldCustomer}
              />
              <InputFailureTracker
                productName={INSURANCE_NAME_VALUE.HEALTH_WITHOUT_SIGNING}
                stepId={HEALTH_ABOUT_YOU_STEP.id}
              />
            </Form>
          );
        }}
      </ProductIntroTile>
    </Sentry.ErrorBoundary>
  );
};
